import { List, useListContext, CreateButton, TopToolbar, useGetList } from 'react-admin';
import { Box, Card, CardContent, Typography, Stack, Chip, Avatar, Divider, TextField, InputAdornment, MenuItem } from '@mui/material';
import PhoneIcon    from '@mui/icons-material/Phone';
import EmailIcon    from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SearchIcon   from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import { card, GOLD, NAVY, layout, inputSx, typo, clickableA11y, badgeStyle, CUSTOMER_SOURCE } from '../styles/ds';
import { useFitCount } from '../hooks/useFitCount';
import FitPagination from '../components/FitPagination';

const ListActions = () => (
  <TopToolbar>
    <CreateButton label="לקוח חדש" />
  </TopToolbar>
);

const countryFlag = country => {
  const l = (country || '').toLowerCase();
  if (l === 'israel' || l === 'ישראל') return '🇮🇱';
  if (l === 'usa' || l === 'united states' || l === 'us') return '🇺🇸';
  return '';
};

const CustomerCards = () => {
  // Server-side search + tag filter — searches ALL customers, not just the
  // loaded page (backend supports filter.q and filter.tag_id)
  const { data = [], isLoading, filterValues, setFilters } = useListContext();
  const navigate = useNavigate();
  const [search, setSearch] = useState(filterValues?.q || '');
  const { data: tags = [] } = useGetList('tags', { pagination: { page: 1, perPage: 100 } });
  const [page, setPage] = useState(1);
  const { fitCount, renderCount, containerRef, startIndex } = useFitCount({ fallback: 12, page });

  const applySearch = (value) => {
    setSearch(value);
    setFilters({ ...filterValues, q: value || undefined });
  };
  const applyTag = (value) => {
    setFilters({ ...filterValues, tag_id: value || undefined });
  };
  const applySource = (value) => {
    setFilters({ ...filterValues, source: value || undefined });
  };

  // A new server-side filter result (search/tag) is a different set —
  // always land back on page 1 rather than a now-meaningless page number.
  useEffect(() => { setPage(1); }, [data]);

  const filtered = data;
  const pageItems = useMemo(() => {
    return filtered.slice(startIndex, startIndex + renderCount);
  }, [filtered, startIndex, renderCount]);

  if (isLoading) return <Box className="crm-page" p={3}><Typography>טוען...</Typography></Box>;

  return (
    <Box>
      <Stack className="crm-wrap crm-filter-row crm-list-search" direction="row" spacing={2} px={2} pt={2} flexWrap="wrap" useFlexGap>
        <TextField
          size="small"
          placeholder="חיפוש לפי שם, טלפון או אימייל..." aria-label="חיפוש לקוחות"
          value={search}
          onChange={e => applySearch(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} /></InputAdornment>,
            sx: { ...inputSx, direction: 'rtl' },
          }}
          sx={{ width: 340, mb: 2 }}
        />
        {tags.length > 0 && (
          <TextField
            select size="small" label="תגית"
            value={filterValues?.tag_id || ''}
            onChange={e => applyTag(e.target.value)}
            sx={{ width: 180, mb: 2 }}
          >
            <MenuItem value="">הכל</MenuItem>
            {tags.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
          </TextField>
        )}
        <TextField
          select size="small" label="מקור"
          value={filterValues?.source || ''}
          onChange={e => applySource(e.target.value)}
          sx={{ width: 160, mb: 2 }}
        >
          <MenuItem value="">הכל</MenuItem>
          {Object.entries(CUSTOMER_SOURCE).map(([id, { label }]) => <MenuItem key={id} value={id}>{label}</MenuItem>)}
        </TextField>
      </Stack>

      {filtered.length === 0 && (
        <Box sx={{ p: 4, mx: 2, mb: 2, textAlign: 'center', borderRadius: 2,
          border: '1.5px dashed rgba(13,29,62,0.18)', backgroundColor: 'rgba(13,29,62,0.015)' }}>
          <Typography color="text.secondary">{search || filterValues?.tag_id || filterValues?.source ? 'לא נמצאו תוצאות' : 'אין לקוחות עדיין'}</Typography>
        </Box>
      )}

      <Box className="crm-form-grid crm-card-grid" ref={containerRef} sx={{ ...layout.cardGrid, gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', pt: 0, minHeight: 200 }}>
        {pageItems.map((customer) => {
          const initials = `${customer.first_name?.[0] ?? ''}${customer.last_name?.[0] ?? ''}`.toUpperCase();
          const fullName = `${customer.first_name ?? ''} ${customer.last_name ?? ''}`.trim();
          return (
            <Card key={customer.id} onClick={() => navigate(`/customers/${customer.id}/show`)}
              {...clickableA11y(() => navigate(`/customers/${customer.id}/show`), { ariaLabel: `פתיחת לקוח ${fullName || ''}`, asButton: true })}
              sx={{ ...card.clickable, border: '1px solid rgba(13,29,62,0.06)' }}>
              <CardContent>
                <Stack className="crm-header" direction="row" justifyContent="space-between" alignItems="center">
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar sx={{ backgroundColor: NAVY, width: 40, height: 40, fontSize: 15 }}>
                      {initials}
                    </Avatar>
                    <Box>
                      <Typography fontWeight={700} fontSize={15} color="primary.main">{fullName}</Typography>
                      {customer.country && (
                        <Typography variant="caption" color="text.secondary">
                          {countryFlag(customer.country)} {customer.country}
                          {customer.city ? `, ${customer.city}` : ''}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    {customer.source && customer.source !== 'crm' && (
                      <span style={badgeStyle(CUSTOMER_SOURCE, customer.source)}>{CUSTOMER_SOURCE[customer.source].label}</span>
                    )}
                    <Chip label="צפה" size="small" sx={{ backgroundColor: 'rgba(13,29,62,0.08)', fontSize: typo.meta.fontSize }} />
                  </Stack>
                </Stack>

                <Divider sx={{ my: 1.5 }} />

                <Stack spacing={0.8}>
                  {customer.phone && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <PhoneIcon sx={{ fontSize: 15, color: GOLD }} />
                      <Typography variant="body2">{customer.phone}</Typography>
                    </Stack>
                  )}
                  {customer.email && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <EmailIcon sx={{ fontSize: 15, color: GOLD }} />
                      <Typography variant="body2" color="text.secondary">{customer.email}</Typography>
                    </Stack>
                  )}
                  {(customer.city || customer.country) && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <LocationOnIcon sx={{ fontSize: 15, color: GOLD }} />
                      <Typography variant="body2" color="text.secondary">
                        {[customer.city, customer.country].filter(Boolean).join(', ')}
                      </Typography>
                    </Stack>
                  )}
                </Stack>

                <Box mt={1.5}>
                  <Chip label={`# ${customer.id}`} size="small"
                    sx={{ backgroundColor: 'rgba(13,29,62,0.07)', fontSize: typo.meta.fontSize }} />
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Box>
      <FitPagination page={page} setPage={setPage} total={filtered.length} perPage={fitCount} />
    </Box>
  );
};

const CustomerList = () => (
  <List className="crm-mobile-list crm-inline-list" actions={<ListActions />} title="לקוחות" perPage={1000} pagination={false} storeKey={false}>
    <CustomerCards />
  </List>
);

export default CustomerList;
